/* ================================================================
   Factory functions for creating default model objects.
   Mirrors legacy DefaultLayerData and defaultDesignData semantics.
   ================================================================ */

import type {
  ProjectModel, ProjectMeta, Side, Layer, SideId, LayerStyle,
  StyleSetting, SizeGroups, OpeningConfig, BlockBoardConfig,
  Dimensions3, Vec3, SideSpanOverride,
  PositionLogic, StyleLogics, Directions, Counts, CornerPackConfig,
} from './types';
import { SIDE_ID_LIST, SIDE_PAIR } from './types';

let _uid = 1;
export function uid(): string {
  return `el_${Date.now()}_${_uid++}`;
}

// --------------- Dimension helpers ---------------

export function dim3(l = 0, w = 0, h = 0): Dimensions3 {
  return { length: l, width: w, height: h };
}

export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}

// --------------- Default StyleSetting ---------------

function defaultPositionLogic(): PositionLogic {
  return { bottom: 0, toppos: 0, sidepos: 0, endpos: 0 };
}

function defaultStyleLogics(): StyleLogics {
  return {
    position: defaultPositionLogic(),
    size: { frame: 1, horBrace: 1, board: 1, bevelBrace: 1 },
    space: { frame: 1, horBrace: 1, board: 1 },
    count: { horBrace: 1, bevelBrace: 1 },
  };
}

function defaultDirections(dir: 0 | 1 = 0): Directions {
  return {
    mountain: 0,
    frame: dir,
    isHor: dir,
    horBrace: dir === 0 ? 1 : 0,
    toward: [],
  };
}

function defaultCornerPack(): CornerPackConfig {
  return { corner: false, pack: { num: 0, space: 50 } };
}

function defaultCounts(style: LayerStyle): Counts {
  let frame: number[] = [];
  let board = 3;
  switch (style) {
    case 0: board = 3; break;
    case 1: board = 1; break;
    case 3: frame = [2, 2]; board = 1; break; // 田字: 边框 + 1竖撑 + 横撑
    case 4: frame = [3, 3]; board = 0; break;
    default: board = 0; frame = [2, 2]; break;
  }
  const horBrace = style === 3 ? [1] : [0];
  return { frame, board, horBrace, bevelBrace: [1] };
}

export function defaultStyleSetting(
  style: LayerStyle = 0,
  direction: 0 | 1 = 0,
  materialType = 4,
  materialColor = '',
): StyleSetting {
  return {
    style,
    material: materialType,
    materialcolor: materialColor,
    CornerPack: defaultCornerPack(),
    logics: defaultStyleLogics(),
    directions: defaultDirections(direction),
    count: defaultCounts(style),
  };
}

// --------------- Default Layer ---------------

function defaultSizeGroups(): SizeGroups {
  return { board: [], frame: [[], []], horBrace: [[]] };
}

function defaultOpenings(_style: LayerStyle): OpeningConfig {
  return { depth: 0, margins: 0, radius: 0, span: 0, space: 0 };
}

export function defaultBlockBoard(style: LayerStyle): BlockBoardConfig {
  if (style === 4) {
    // 托盘底部粗板条: 显式宽度 90mm, 3 根
    return { boardLength: 0, boardWidth: 90, count: 3, centerSpacing: 0 };
  }
  if (style === 0) {
    // 普通板条: 默认宽 100mm, 默认 3 根
    return { boardLength: 0, boardWidth: 100, count: 3, centerSpacing: 0 };
  }
  if (style === 3) {
    // 田字: 板条部分默认 1 根横撑
    return { boardLength: 0, boardWidth: 0, count: 1, centerSpacing: 0 };
  }
  return { boardLength: 0, boardWidth: 0, count: 0, centerSpacing: 0 };
}

export function defaultThickness(style: LayerStyle, sideId?: SideId): number {
  const isSideWall = sideId && sideId !== 'pedestal' && sideId !== 'topCap';
  if (style === 4) return isSideWall ? 18 : 90;
  if (style === 3) return 18; // 田字框架
  if (style === 1) return 12;
  if (style === 0) return isSideWall ? 18 : 100;
  return 18;
}

export function createDefaultLayer(
  sideId: SideId,
  layerIndex: number,
  style: LayerStyle = 0,
  direction: 0 | 1 = 0,
): Layer {
  const sideIdx = SIDE_ID_LIST.indexOf(sideId);
  const thickness = defaultThickness(style, sideId);

  return {
    id: uid(),
    name: `第 ${layerIndex + 1} 层`,
    style,
    profile: styleLabel(style),
    direction,
    thickness,
    materialKey: 4,
    color: '#c8a96e',
    offset: vec3(),
    mirrored: false,
    styleSetting: defaultStyleSetting(style, direction),
    sizeGroups: defaultSizeGroups(),
    openings: defaultOpenings(style),
    blockBoard: defaultBlockBoard(style),
    isBase: layerIndex === 0,
    recordsNo: sideIdx * 10 + layerIndex,
    layerIndex,
  };
}

export function styleLabel(s: LayerStyle): string {
  switch (s) {
    case 0: return '板条';
    case 1: return '整板';
    case 3: return '田字';
    case 4: return '木方';
    default: return '未知';
  }
}

// --------------- Default Side ---------------

export function createDefaultSide(
  sideId: SideId,
  style: LayerStyle = 1,
  direction: 0 | 1 = 0,
  enabled = true,
): Side {
  return {
    sideId,
    pairSideId: SIDE_PAIR[sideId],
    enabled,
    layers: [createDefaultLayer(sideId, 0, style, direction)],
  };
}

// --------------- Default Project ---------------

function createInitialSideSpanOverrides(
  innerSize: Dimensions3,
  outerSize: Dimensions3,
): Partial<Record<SideId, SideSpanOverride>> {
  return {
    pedestal: { spanU: outerSize.length, spanV: outerSize.width, offsetU: 0, offsetV: 0 },
    topCap: { spanU: outerSize.length, spanV: outerSize.width, offsetU: 0, offsetV: 0 },
    sideFront: { spanU: outerSize.length, spanV: innerSize.height, offsetU: 0, offsetV: 0 },
    sideBack: { spanU: outerSize.length, spanV: innerSize.height, offsetU: 0, offsetV: 0 },
    endLeft: { spanU: outerSize.width, spanV: innerSize.height, offsetU: 0, offsetV: 0 },
    endRight: { spanU: outerSize.width, spanV: innerSize.height, offsetU: 0, offsetV: 0 },
  };
}

/**
 * 木箱默认结构:
 *   6 面全部启用，每面 1 层整板 (style=1)
 *
 * 托盘默认结构:
 *   底座: 板条 (style=0, 三根纵向枕木)
 *   顶盖: 板条 (style=0, 横向铺设，与底座交错)
 *   四个侧面: 禁用
 */
export function createDefaultProject(mode: 'crate' | 'pallet' = 'crate'): ProjectModel {
  const now = new Date().toISOString();
  const meta: ProjectMeta = {
    id: uid(),
    name: mode === 'pallet' ? '新建托盘' : '新建木箱',
    customerName: '',
    mode,
    createdAt: now,
    updatedAt: now,
  };

  if (mode === 'pallet') {
    // 托盘: 扁平结构, 底座(粗板条 90×90mm) + 顶部(薄板条 20mm)
    // 外尺寸高度在下面根据实际层厚度自动计算
    const innerSize = dim3(1200, 1000, 0);
    const goodsSize = dim3(1200, 1000, 0);

    const sides: Side[] = [
      createDefaultSide('pedestal', 0, 0, true),     // 底座: 板条纵向
      createDefaultSide('sideFront', 1, 0, false),   // 前面: 禁用
      createDefaultSide('endLeft', 1, 0, false),     // 左端: 禁用
      createDefaultSide('endRight', 1, 0, false),    // 右端: 禁用
      createDefaultSide('sideBack', 1, 0, false),    // 后面: 禁用
      createDefaultSide('topCap', 0, 1, true),       // 顶盖: 板条横向，与底座交错
    ];

    // 托盘底座：三根纵向枕木，100mm厚，100mm宽
    const bottomLayer = sides[0].layers[0];
    bottomLayer.styleSetting.count.board = 3;
    bottomLayer.thickness = 100;
    bottomLayer.blockBoard = { boardLength: 0, boardWidth: 100, count: 3, centerSpacing: 0 };

    // 托盘顶部铺板：薄板条横向，默认 5 条，20mm厚
    const topLayer = sides[5].layers[0];
    topLayer.styleSetting.count.board = 5;
    topLayer.blockBoard.count = 5;
    topLayer.blockBoard.boardWidth = 0;
    topLayer.thickness = 20;

    // 外尺寸 = 内尺寸 + 各面层厚度
    const outerSize = dim3(
      innerSize.length, // 侧面禁用，长宽不变
      innerSize.width,
      innerSize.height + bottomLayer.thickness + topLayer.thickness,
    );

    return {
      meta,
      innerSize,
      outerSize,
      goodsSize,
      payloadKg: 1000,
      grossWeightKg: 0,
      symmetry: { side: true, end: true },
      selfAdaption: true,
      sideLinkageMode: 'independent',
      sideSpanOverrides: createInitialSideSpanOverrides(innerSize, outerSize),
      sides,
    };
  }

  // 木箱: 6 面整板封闭箱体
  const innerSize = dim3(1200, 800, 600);
  const outerSize = dim3(1224, 824, 624);
  const goodsSize = dim3(1200, 800, 600);

  const sides: Side[] = [
    createDefaultSide('pedestal', 1, 0, true),    // 底座: 整板
    createDefaultSide('sideFront', 1, 0, true),   // 前面: 整板
    createDefaultSide('endLeft', 1, 0, true),     // 左端: 整板
    createDefaultSide('endRight', 1, 0, true),    // 右端: 整板
    createDefaultSide('sideBack', 1, 0, true),    // 后面: 整板
    createDefaultSide('topCap', 1, 0, true),      // 顶盖: 整板
  ];

  return {
    meta,
    innerSize,
    outerSize,
    goodsSize,
    payloadKg: 100,
    grossWeightKg: 0,
    symmetry: { side: true, end: true },
    selfAdaption: true,
    sideLinkageMode: 'independent',
    sideSpanOverrides: createInitialSideSpanOverrides(innerSize, outerSize),
    sides,
  };
}
